import cn from 'classnames';
import dynamic from 'next/dynamic';
import type { SyntheticEvent } from 'react';
import React from 'react';

import { Text } from '@sravni/react-design-system/lib/Typography';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import type { CommonCardPropsMeta } from '@src/@types/commonCardProps';
import { Currency } from '@src/@types/currency';
import { Pixel } from '@src/components/Pixel';
import { sendOpenCardDetailsInModalEvent } from '@src/helpers/analyticsEvents';
import { LightweightCardView } from 'entities/Microcredits/ui';
import { useCardAction } from 'entities/Microcredits/ui/CardActionProvider';
import type {
  LightweightCardActionsArgs,
  LightweightCardContentSettings,
  LightweightCardOnClickArgs,
} from 'entities/Microcredits/ui/LightweightCard';
import { getCardBadge } from 'entities/Microcredits/ui/LightweightCard';
import { ContentBankruptcy } from 'features/Bankruptcy/ui/ContentBankruptcy';
import { LightweightCardAction } from 'features/Microcredits/ui/LightweightCardAction/LightweightCardAction';
import { AdaptiveText } from 'shared/ui';
import { modalService } from 'widgets/GlobalDialog';

import { useCardAnalytics } from '../lib';

import { DEFAULT_CONTENT, getCardContent, PIXEL_DISPLAY_WRAPPER_STYLES } from './LightweightCard.config';
import styles from './LightweightCard.module.scss';

const CardDetailsModal = dynamic(
  () => import('entities/Microcredits/ui/CardDetailsModal').then((r) => r.CardDetailsModal),
  { ssr: false },
);

export interface Props {
  meta?: CommonCardPropsMeta;
  offer: IClientMicrocreditListItem;
  position: number;

  hasUtmLabel?: boolean;

  contentSettings?: LightweightCardContentSettings;
  currency?: Currency;
  showPromo?: boolean;

  renderActions?: (args: LightweightCardActionsArgs) => React.ReactNode;
  onClick?: (e: SyntheticEvent, args: LightweightCardOnClickArgs) => void;
}

export const LightweightCard = ({
  offer,
  currency = Currency.RUB,
  position,
  hasUtmLabel,
  contentSettings = DEFAULT_CONTENT,
  onClick,
  renderActions,
  meta,
}: Props) => {
  const cardAnalytics = useCardAnalytics(offer, position, meta, onClick);

  const { source, affSub4, pixelDisplayLink, openMfoSite } = cardAnalytics;

  const action = useCardAction(openMfoSite, {
    offer,
    currency,
    position,
    hasUtmLabel,
    contentSettings,
    isMobile: meta?.isMobile,
  });

  const handleOpenModal = async (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const { organization, advertising, name } = offer;
    const eventSource = `position_${advertising?.position}`;
    const eventLabel = `${organization?.name}|${name}|${advertising?.offerId}|${eventSource}`;

    sendOpenCardDetailsInModalEvent(eventLabel);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    await modalService.open({ component: CardDetailsModal, props: { offer, isMobile: meta?.isMobile } });
  };

  const content = getCardContent(offer, currency, meta?.isMobile, contentSettings);
  const badge = contentSettings.badge.visible
    ? getCardBadge({ offer, hasUtmLabel, styles, settings: contentSettings.badge })
    : null;

  const isShowPromoBanner = meta?.isPromoActive && meta?.isMobile && offer?._features?.isSravniOffer;

  const isBankruptcyCard = !!offer?._features?.isBankruptcyOffer;

  if (isBankruptcyCard) {
    return (
      <LightweightCardView
        offer={offer}
        customContent={<ContentBankruptcy />}
        actions={
          <LightweightCardAction.Container>
            <LightweightCardAction.ActionButton onClick={action}>Далее</LightweightCardAction.ActionButton>
          </LightweightCardAction.Container>
        }
        pixelDisplay={
          <Pixel
            wrapperStyles={PIXEL_DISPLAY_WRAPPER_STYLES}
            source={meta.source ?? source}
            link={pixelDisplayLink}
            affSub4={{ ...affSub4, ...meta.affSub4 }}
          />
        }
        promo={
          isShowPromoBanner && (
            <AdaptiveText
              longText="Дарим 500₽ за займ"
              shortText="Дарим 500₽ за займ"
              renderText={(props) => (
                <Text size={12} {...props} className={cn(styles.fullDealLabel, props.className)} />
              )}
            />
          )
        }
      />
    );
  }

  return (
    <LightweightCardView
      offer={offer}
      content={content}
      badge={badge}
      actions={
        renderActions ? (
          renderActions({ onClick: action, onOpenModal: handleOpenModal, analytics: cardAnalytics })
        ) : (
          <LightweightCardAction.Container>
            {meta?.withCardDetailsClick && !hasUtmLabel && <LightweightCardAction.TipModal onClick={handleOpenModal} />}
            <LightweightCardAction.ActionButton onClick={action} />
          </LightweightCardAction.Container>
        )
      }
      pixelDisplay={
        <Pixel wrapperStyles={PIXEL_DISPLAY_WRAPPER_STYLES} source={source} link={pixelDisplayLink} affSub4={affSub4} />
      }
      promo={
        isShowPromoBanner && (
          <AdaptiveText
            longText="Дарим 500₽ за займ"
            shortText="Дарим 500₽ за займ"
            renderText={(props) => <Text size={12} {...props} className={cn(styles.fullDealLabel, props.className)} />}
          />
        )
      }
    />
  );
};
